// Shared utilities for mention functionality
// This file breaks the circular dependency between Markdown and Input components
export const MENTION_TRIGGER = '@';
export const MENTION_END_CHAR_REGEX = /\s|[,;(){}?]/;
export const MENTION_PROPERTIES_SEPARATOR = ':';
/**
 * Entity types that default to 'name' display (showing last segment after dot).
 */ const NAME_DISPLAY_ENTITY_TYPES = [
    'dataset',
    'pipeline'
];
function getDefaultDisplayMode(entityType) {
    return NAME_DISPLAY_ENTITY_TYPES.includes(entityType.toLowerCase()) ? 'name' : 'path';
}
/**
 * Extract display text from an entity ID based on display mode.
 * - 'path': Returns the full ID as-is.
 * - 'name': If the ID contains dots (e.g., database.schema.table), returns the last segment.
 */ function getDisplayTextFromEntityId(entityId, display) {
    if (display === 'name' && entityId.includes('.')) {
        const parts = entityId.split('.');
        return parts[parts.length - 1];
    }
    return entityId;
}
/**
 * Parse a mention string that may include base64-encoded properties.
 * Format: entityType:entityId or entityType:entityId:base64EncodedProperties
 * Example: gem:Transform_1:eyJwaXBlbGluZSI6InAxIn0=
 */ export function parseMentionWithProperties(mentionText) {
    const parts = mentionText.split(MENTION_PROPERTIES_SEPARATOR);
    if (parts.length < 2) {
        return null;
    }
    const entityType = parts[0];
    const entityId = parts[1];
    let properties;
    if (parts.length >= 3) {
        const propertiesBase64 = parts.slice(2).join(MENTION_PROPERTIES_SEPARATOR);
        try {
            const decodedProperties = atob(propertiesBase64);
            properties = JSON.parse(decodedProperties);
        } catch  {
        // Invalid base64 or JSON, ignore properties
        }
    }
    const displayMode = properties?.display ?? getDefaultDisplayMode(entityType);
    const displayText = getDisplayTextFromEntityId(entityId, displayMode);
    const isShortened = displayText !== entityId;
    // If display is shortened and fullPath isn't already set, add it for tooltip
    if (isShortened && !properties?.fullPath) {
        properties = {
            ...properties,
            fullPath: entityId
        };
    }
    return {
        entityType,
        entityId,
        displayText,
        properties,
        entityName: displayText.trimEnd()
    };
}
/**
 * Enrich a mention string with additional properties.
 * Parses existing properties (if any), merges with new properties, and re-encodes.
 * @param mentionText - The mention string (without @ prefix), e.g., "gem:Transform_1" or "gem:Transform_1:base64props"
 * @param additionalProperties - Properties to merge into the mention
 * @returns The enriched mention string
 */ export function enrichMentionWithProperties(mentionText, additionalProperties) {
    const parsed = parseMentionWithProperties(mentionText);
    if (!parsed) {
        return mentionText;
    }
    const { entityType, entityId, properties: existingProperties } = parsed;
    // These are inferred properties computed at render time - don't persist them
    const { fullPath: _existingFullPath, category: _existingCategory, component: _existingComponent, processId: _existingProcessId, ...cleanExistingProperties } = existingProperties || {};
    const finalProperties = {
        ...cleanExistingProperties,
        ...additionalProperties
    };
    const hasProperties = Object.keys(finalProperties).length > 0;
    let enrichedMention = `${entityType}${MENTION_PROPERTIES_SEPARATOR}${entityId}`;
    if (hasProperties) {
        const propertiesBase64 = btoa(JSON.stringify(finalProperties));
        enrichedMention += `${MENTION_PROPERTIES_SEPARATOR}${propertiesBase64}`;
    }
    return enrichedMention;
}
/**
 * Given a text and a start index, return the mention value and the end index of the first mention
 */ export function getMentionValue(text, startIndex) {
    let inQuote = false;
    let quoteChar = '';
    let mentionValue = '';
    let j = startIndex;
    // Process characters until we hit a space (outside quotes) or end of text
    while(j < text.length){
        // Handle quote characters
        if (!inQuote && (text[j] === "'" || text[j] === '"' || text[j] === '`')) {
            inQuote = true;
            quoteChar = text[j];
            mentionValue += text[j];
            j++;
            continue;
        }
        // Handle end of quote
        if (inQuote && text[j] === quoteChar) {
            inQuote = false;
            mentionValue += text[j];
            j++;
            continue;
        }
        // If we're outside quotes and hit a space or punctuation character (except . and :)
        //  or brackets (except the square branches, which is used for index), we're done
        if (!inQuote && MENTION_END_CHAR_REGEX.test(text[j])) {
            break;
        }
        // ignore trailing dots
        if (text[j] === '.' && (j === text.length - 1 || text[j + 1] === ' ')) {
            break;
        }
        // Add the character to our mention value
        mentionValue += text[j];
        j++;
    }
    return {
        value: mentionValue,
        endIndex: j
    };
}
